/**
 * How an HTML file record presents on a canvas.
 *
 * The file record is the source of truth (`metadata.display`); the canvas
 * node carries a synced copy so the card can be routed synchronously from
 * room storage — paint never waits on record hydration. This module is the
 * ONE place that reads the node copy, in both shapes it has ever had:
 *
 *   data.display      — the current model: `{mode, chromeless, layer}`
 *   data.presentation — the legacy marker `{frameless, transparent, layer,
 *                       interactive}`, whose presence meant "block"
 *
 * The legacy read is permanent: existing canvases and published snapshots
 * carry the old shape and are never rewritten. Keeping the mapping here means
 * retiring it — if ever — is a one-function change.
 */

export type BlockDisplayMode = "block" | "document";
export type BlockLayer = "front" | "back";

export interface BlockDisplay {
  mode: BlockDisplayMode;
  /** No card chrome and no background — the HTML composites directly onto
   *  the canvas ground. Blocks only. */
  chromeless: boolean;
  /** Stacking intent, a per-placement fact. Consumed by the backend at
   *  placement time (node z-index); carried here for completeness. */
  layer: BlockLayer;
}

/** The legacy node marker, kept only as the normalizer's input type. */
export interface LegacyBlockPresentation {
  frameless?: boolean;
  transparent?: boolean;
  layer?: BlockLayer;
  interactive?: boolean;
}

let legacyReads = 0;
let legacyReadLogged = false;

/** How many times the legacy `data.presentation` branch has fired in this
 *  session — the signal for whether the mapping is still load-bearing. */
export function legacyBlockMarkerReadCount(): number {
  return legacyReads;
}

function noteLegacyRead(): void {
  legacyReads += 1;
  if (legacyReadLogged) return;
  legacyReadLogged = true;
  if (typeof console !== "undefined" && typeof console.info === "function") {
    console.info(
      "[cohesive] rendering a block from the legacy data.presentation marker",
    );
  }
}

function layerOf(value: unknown): BlockLayer {
  return value === "back" ? "back" : "front";
}

/**
 * Resolve a node's block display from its data, or null when the node is not
 * an HTML block (documents, images, everything else). Presence of either
 * marker with block mode is what routes a node to the block card.
 */
export function blockDisplayFromNodeData(data: unknown): BlockDisplay | null {
  if (!data || typeof data !== "object") return null;
  const record = data as {
    display?: unknown;
    presentation?: unknown;
  };

  const display = record.display;
  if (display && typeof display === "object") {
    const d = display as {
      mode?: unknown;
      chromeless?: unknown;
      layer?: unknown;
    };
    if (d.mode === "block" || d.mode === "document") {
      return {
        mode: d.mode,
        chromeless: d.mode === "block" && d.chromeless === true,
        layer: layerOf(d.layer),
      };
    }
  }

  // Legacy: the marker's PRESENCE meant block (an empty object is a framed
  // block with all defaults). `interactive` is dropped — interactivity is
  // detected empirically now.
  const presentation = record.presentation;
  if (presentation != null && typeof presentation === "object") {
    noteLegacyRead();
    const p = presentation as LegacyBlockPresentation;
    return {
      mode: "block",
      chromeless: p.frameless === true || p.transparent === true,
      layer: layerOf(p.layer),
    };
  }

  return null;
}

/** The part of a file record this module reads: `metadata.display` as the
 *  API renders it. */
export interface BlockDisplayRecord {
  display?: { mode?: unknown; chromeless?: unknown } | null;
}

/**
 * Resolve a node's block display with the file record first. The record
 * holds the identity (mode, chromeless; the kind is read off it elsewhere),
 * the node holds the instance (layer), and the node's synced copy stands in
 * while the record is still hydrating or the client has none (the public
 * viewer). A record with no display, or one that is not yet in, falls back
 * to the node copy; a record that says document wins over a node copy that
 * says block, so a display change reaches every node without a rewrite.
 */
export function resolveBlockDisplay(
  record: BlockDisplayRecord | null | undefined,
  nodeData: unknown,
): BlockDisplay | null {
  const fromNode = blockDisplayFromNodeData(nodeData);
  const d = record?.display;
  if (d && (d.mode === "block" || d.mode === "document")) {
    return {
      mode: d.mode,
      chromeless: d.mode === "block" && d.chromeless === true,
      layer: fromNode?.layer ?? "front",
    };
  }
  return fromNode;
}

/**
 * True when the node renders as a live block: the same resolved display the
 * node router paints from, so a record that became a document is no longer
 * expandable anywhere, and one that became a block expands before its node
 * copy catches up. Without the record, the node copy answers alone.
 */
export function isBlockNodeData(
  data: unknown,
  record?: BlockDisplayRecord | null,
): boolean {
  return resolveBlockDisplay(record, data)?.mode === "block";
}

/**
 * The display a node's synced copy should be rewritten to from its record,
 * or null: the record says nothing, the copy already agrees, or the record
 * says document and the node has no copy (the backend stamps none for a
 * document, so that is not a stale copy). The layer stays the node's.
 */
export function staleDisplayCopy(
  record: BlockDisplayRecord | null | undefined,
  nodeData: unknown,
): BlockDisplay | null {
  const d = record?.display;
  if (!d || (d.mode !== "block" && d.mode !== "document")) return null;
  const copy = blockDisplayFromNodeData(nodeData);
  if (!copy && d.mode === "document") return null;
  const want: BlockDisplay = {
    mode: d.mode,
    chromeless: d.mode === "block" && d.chromeless === true,
    layer: copy?.layer ?? "front",
  };
  if (copy && copy.mode === want.mode && copy.chromeless === want.chromeless) {
    return null;
  }
  return want;
}
